import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    TimelinePositionContextType,
    TimelinePartContentProps,
    TimelineContextType
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const TIMELINE_DEFAULT_POSITION: TimelinePositionContextType = {
    isDayGrouped: false,
    isFinal: false,
    isToday: false,
    feedCount: -1,
    isLast: false,
    feedIndex: 0
};

export const TimelineContext = createContext<TimelineContextType | undefined>(undefined);

export const TimelinePositionContext = createContext<TimelinePositionContextType>(TIMELINE_DEFAULT_POSITION);

export const useTimelineContext = ({
    part
}: {
    part: string;
}): TimelineContextType | undefined => {
    const context = useContext(TimelineContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Timeline.${part}: must be rendered inside <Timeline>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withTimelineContext = <P extends object>(
    part: string,
    Content: ComponentType<TimelinePartContentProps<P>>
): FC<P> => {
    const TimelinePart: FC<P> = (props) => {
        const context = useTimelineContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    TimelinePart.displayName = `Timeline.${part}`;

    return TimelinePart;
};
